import { memo, useState, useEffect, useRef, useMemo } from 'react';
import { NodeResizer } from '@xyflow/react';
import ContentEditable from 'react-contenteditable';
import { stripHtml } from '../../components/StripHtml';
import ActivityEnergyLayer, {
  getEnergyMeterClass,
} from '../../components/energy-meter/ActivityEnergyLayer';

const ArrowBoxNode = ({ data, id, selected, editable, langCode }) => {
  const [label, setLabel] = useState(stripHtml(data.label || ''));
  const isSelected = selected;
  const textareaRef = useRef(null);

  const [width, setWidth] = useState(data.width_height?.width || 326);
  const [height, setHeight] = useState(data.width_height?.height || 90);

  const nodeId = id || data.id;
  const em = data?.em;
  const emClass = getEnergyMeterClass(em, nodeId);

  // Sync size
  useEffect(() => {
    if (data.width_height) {
      setWidth(data.width_height.width);
      setHeight(data.width_height.height);
    }
  }, [data.width_height]);

  // Sync label (only when not focused to preserve cursor and space typing)
  useEffect(() => {
    const isFocused = textareaRef.current && document.activeElement === textareaRef.current;
    if (!isFocused) {
      setLabel(stripHtml(data.label || ''));
    }
  }, [data.label]);

  // Auto focus on edit mode when selected
  useEffect(() => {
    if (editable && isSelected && textareaRef.current) {
      const timeout = setTimeout(() => {
        textareaRef.current?.focus();

        // Move caret to the end of content
        const el = textareaRef.current;
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [isSelected, editable]);

  const handleChange = (e) => {
    setLabel(e.target.value);
    data.onLabelChange?.(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak');
    }
  };

  const clipPath = useMemo(() => {
    const arrowWidth = 24;
    return `polygon(
      20px 50%,
      0 0,
      ${width - arrowWidth}px 0,
      ${width}px 50%,
      ${width - arrowWidth}px 100%,
      0 100%
    )`;
  }, [width]);

  // Resize handling
  const GRID = 20;
  const snap = (v) => Math.round(v / GRID) * GRID;

  const handleResize = (_, size) => {
    if (!size) return;
    const w = snap(size.width);
    const h = snap(size.height);
    setWidth(w);
    setHeight(h);
    data.updateWidthHeight?.(id, { width: w, height: h });
  };

  const handleNodeClick = (e) => {
    if (!editable && em && em.active) {
      e.stopPropagation();
      if (em.mode === 'analysis') {
        em.onOpenPopover({ id: nodeId, title: label });
      }
    }
  };

  return (
    <div
      style={{
        position: 'relative',
        filter: data.hasNextLevel ? 'drop-shadow(0px 0px 10px #0000004f)' : 'none',
      }}
      onClick={handleNodeClick}
    >
      {!editable && <ActivityEnergyLayer data={data} activityId={nodeId} activityTitle={label} />}
      <div
        className={!editable ? `borderBox ${emClass}` : ''}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width,
          height,
          clipPath,
          background: 'red',
          padding: '10px 25px',
          boxSizing: 'border-box',
        }}
      >
        {editable ? (
          <ContentEditable
            innerRef={textareaRef}
            html={label}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="nodrag rb-process-model-hyphenated-text"
            lang={(langCode || 'de').toLowerCase()}
            style={styles.textarea}
          />
        ) : (
          <div
            className="rb-process-model-hyphenated-text"
            lang={(langCode || 'de').toLowerCase()}
            style={styles.textLabel}
          >
            {label}
          </div>
        )}
      </div>

      {editable && isSelected && (
        <NodeResizer
          minWidth={120}
          minHeight={80}
          onResize={handleResize}
          handleClassName="customHandle"
          lineClassName="customLine"
        />
      )}
    </div>
  );
};

const styles = {
  textarea: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: 'white',
    caretColor: 'white',
    textAlign: 'center',
    fontSize: '12px',
    lineHeight: '1.1',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    fontFamily: 'Poppins, sans-serif',
    boxSizing: 'border-box',
    cursor: 'text',
    userSelect: 'text',
    WebkitUserSelect: 'text',
    msUserSelect: 'text',
  },
  textLabel: {
    cursor: 'pointer',
    fontSize: '12px',
    lineHeight: '1.1',
    fontFamily: "'Poppins', sans-serif",
    color: 'white',
    textAlign: 'center',
    width: '100%',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    boxSizing: 'border-box',
  },
};

export default memo(ArrowBoxNode);
